import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import {
  Button,
  Chip,
  IconButton,
  makeStyles,
  Paper,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";
import { DeleteOutline, Edit } from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ResponsiveTable from "../../components/ResponsiveTable";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import TagModal from "../../components/TagModal";
import openSocket from "../../services/socket-io";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { contrastText } from "../../helpers/contrastText";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const sortTags = (tags) =>
  [...tags].sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));

const Tags = () => {
  const classes = useStyles();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/tags");
        setTags(data);
      } catch (err) {
        toastError(err);
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    const socket = openSocket();

    socket.on("tag", (data) => {
      if (data.action === "update") {
        setTags((prev) =>
          sortTags([...prev.filter((t) => t.id !== data.tag.id), data.tag])
        );
      }
      if (data.action === "delete") {
        setTags((prev) => prev.filter((t) => t.id !== data.tagId));
      }
    });

    return () => socket.disconnect();
  }, []);

  const handleOpenModal = (tag = null) => {
    setSelectedTag(tag);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedTag(null);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/tags/${selectedTag.id}`);
      toast.success(i18n.t("tags.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setSelectedTag(null);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          selectedTag &&
          `${i18n.t("tags.confirmationModal.deleteTitle")} ${selectedTag.name}?`
        }
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
      >
        {i18n.t("tags.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <TagModal
        open={modalOpen}
        onClose={handleCloseModal}
        tagId={selectedTag?.id}
      />
      <MainHeader>
        <Title>{i18n.t("tags.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleOpenModal()}
          >
            {i18n.t("tags.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <ResponsiveTable>
          <TableHead>
            <TableRow>
              <TableCell align="center">{i18n.t("tags.table.name")}</TableCell>
              <TableCell align="center">{i18n.t("tags.table.kanban")}</TableCell>
              <TableCell align="center">{i18n.t("tags.table.position")}</TableCell>
              <TableCell align="center">{i18n.t("tags.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tags.map((tag) => (
              <TableRow key={tag.id}>
                <TableCell align="center">
                  <Chip
                    size="small"
                    label={tag.name}
                    style={{
                      backgroundColor: tag.color,
                      color: contrastText(tag.color),
                    }}
                  />
                </TableCell>
                <TableCell align="center" data-label={i18n.t("tags.table.kanban")}>
                  {i18n.t(tag.kanban ? "tags.table.yes" : "tags.table.no")}
                </TableCell>
                <TableCell align="center" data-label={i18n.t("tags.table.position")}>
                  {tag.position}
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleOpenModal(tag)}>
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedTag(tag);
                      setConfirmOpen(true);
                    }}
                  >
                    <DeleteOutline />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={4} />}
          </TableBody>
        </ResponsiveTable>
      </Paper>
    </MainContainer>
  );
};

export default Tags;
